import React, { useEffect, useRef, useState } from "react";
import { denominations } from "../utils";
import { Input } from "./input";
import { Button } from "./button";
import { cn } from "../../lib/utils";

/**
 * Searchable denomination select for signup and org profile identity.
 *
 * @param {Object} props
 * @param {string} [props.value]
 * @param {(denomination: string) => void} props.onChange
 * @param {string} [props.id]
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 * @param {boolean} [props.required]
 * @param {boolean} [props.invalid]
 * @returns {JSX.Element}
 */
export default function DenominationPicker({
  value = "",
  onChange,
  id = "denomination-picker",
  placeholder = "Search or select denomination…",
  className,
  required = false,
  invalid = false,
}) {
  const [search, setSearch] = useState(value || "");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filtered = denominations.filter((d) =>
    d.toLowerCase().includes(search.toLowerCase())
  );

  /**
   * @param {string} denomination
   */
  const select = (denomination) => {
    onChange(denomination);
    setSearch(denomination);
    setOpen(false);
  };

  return (
    <div className={cn("relative", className)} ref={rootRef}>
      <Input
        id={id}
        type="text"
        placeholder={placeholder}
        value={search}
        onChange={(e) => {
          const next = e.target.value;
          setSearch(next);
          setOpen(true);
          // Allow free-text / custom while typing; commit on blur via parent if needed
          onChange(next);
        }}
        onFocus={() => setOpen(true)}
        className={cn("bg-ui-card", invalid && "border-error")}
        autoComplete="off"
        required={required}
        aria-required={required || undefined}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
      />
      {open && filtered.length > 0 ? (
        <div
          id={`${id}-listbox`}
          className="list-scrollbar absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-ui-border bg-ui-card py-0.5 shadow-profile-md"
          role="listbox"
          aria-label="Denominations"
        >
          {filtered.map((denomination) => (
            <Button
              key={denomination}
              type="button"
              variant="ghost"
              role="option"
              aria-selected={value === denomination}
              className="h-auto w-full justify-start rounded-none px-3 py-2 font-normal text-text-primary hover:bg-ui-muted"
              onClick={() => select(denomination)}
            >
              {denomination}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
